// setup.js - CodeMirror-Grundaufbau: Extensions, Markdoc-Konfiguration, initEditor

import { EditorState } from "@codemirror/state";
import { EditorView, placeholder } from "@codemirror/view";
import { history } from "@codemirror/commands";
import { indentOnInput, bracketMatching, foldGutter, syntaxHighlighting } from "@codemirror/language";
import { lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, highlightActiveLine } from "@codemirror/view";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { Table } from "@lezer/markdown";
import richMarkdown from "codemirror-rich-markdoc";
import * as Markdoc from "@markdoc/markdoc";
import customHighlightStyle from "../highlightStyle.js";
import { einrueckungExtension } from "./einrueckung.js";
import { diffView } from "../diff-view.js";
import { codeBlockHighlighter } from "../codeblock-highlighter.js";
import { highlightHighlighter } from "../highlight-highlighter.js";
import { bracketHighlighter } from "../bracket-highlighter.js";
import { tagHighlighter } from "../tag-highlighter.js";
import { listContinuation, listAutoRenumber } from "../list-continuation.js";
import { obsidianAutoWrap } from "./auto-wrap.js";
import { EditorAPI } from "./editor-api.js";
import { setupToolbar } from "./toolbar.js";
import { appKeymap } from "./keymap.js";

// Verwirft den Diff, sobald im Editor weitergearbeitet wird.
const diffClearOnChange = EditorView.updateListener.of((update) => {
  if (update.docChanged) {
    diffView.onEditorChange();
  }
});

// Create Markdoc configuration for custom tags
const markdocConfig = {
  tags: {
    callout: {
      render: "Callout",
      attributes: {
        type: {
          type: String,
          default: "info",
          matches: ["info", "warning", "error"]
        }
      },
      transform(node, config) {
        const children = node.transformChildren(config);
        const kind = node.attributes.type === 'warning' ? 'warning' : 'info';
        const icon = kind === 'warning' ? 'icon-exclamation' : 'icon-info';
        const className = `cm-markdoc-callout cm-markdoc-callout--${kind}`;
        return new Markdoc.Tag('div', { class: className }, [
          new Markdoc.Tag('span', { class: `icon ${icon}` }),
          new Markdoc.Tag('div', {}, children)
        ]);
      }
    },
    // You can add other custom tags here
  },
  nodes: {
    document: {
      render: "Document",
    },
    heading: {
      render: "Heading",
      attributes: {
        level: { type: Number, required: true }
      }
    }
  }
};

// Initialize the editor
export function initEditor(config = {}) {
  const editorEl = document.getElementById('editor');
  if (!editorEl) {
    console.error('Editor element not found');
    return null;
  }
  
  // Build the customSetup based on config options
  const customSetup = [
    ...(config.showLineNumbers !== false ? [lineNumbers()] : []),
    highlightActiveLineGutter(),
    ...(config.showFoldGutter !== false ? [foldGutter()] : []),  // Make fold gutter optional
    highlightSpecialChars(),
    history(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    rectangularSelection(),
    indentOnInput(),
    syntaxHighlighting(customHighlightStyle),
    bracketMatching(),
    highlightActiveLine(),
    appKeymap()
  ];
  
  const state = EditorState.create({
    doc: '', // Empty document initially
    extensions: [
      // Tab/Shift-Tab zum Ein- und Ausruecken - MUSS vor customSetup stehen
      einrueckungExtension(),
      customSetup,
      markdown({
        base: markdownLanguage,
        codeLanguages: languages,
        extensions: [Table]
      }),
      richMarkdown({
        markdoc: markdocConfig,
        lezer: {
          codeLanguages: languages,
          extensions: [Table]
        }      }),
      EditorView.lineWrapping,
      // Add Obsidian-like auto-wrapping
      obsidianAutoWrap,
      // Add code block background highlighting
      codeBlockHighlighter,
      // Add highlight/mark background (==text== syntax)
      highlightHighlighter,
      // Add bracket/purple highlight ([text] syntax)
      bracketHighlighter,
      // Add tag highlighting (#tag syntax)
      tagHighlighter,
      // Add numbered list continuation and auto-renumbering
      listContinuation,
      listAutoRenumber,
      // Add placeholder with gray styling matching editor font
      placeholder('Schreib etwas...', {
        style: `color: rgba(255, 255, 255, 0.3); font-family: 'Inter', sans-serif; font-size: 17px;`
      }),
      // Clear diff when editor content changes
      diffClearOnChange,
      // Remove borders and separator lines
      EditorView.theme({
        ".cm-gutters": {
          borderRight: "none"
        },
        ".cm-editor": {
          border: "none",
          outline: "none"
        },
        ".cm-content": {
          border: "none",
          fontSize: "17px",
          fontFamily: "'Inter', sans-serif"
        },
        ".cm-scroller": {
          border: "none"
        },
        ".cm-editor.cm-focused": {
          outline: "none !important",
          border: "none !important"
        },
        ".cm-editor:focus, .cm-editor:focus-visible": {
          outline: "none !important",
          border: "none !important"
        }
      })
    ]
  });
  
  const view = new EditorView({ state, parent: editorEl });
  view.focus();
    // Create API instance
  const api = new EditorAPI(view);

  // Make API globally available for hotkeys
  window.editorAPI = api;

  // Set up toolbar functionality
  setupToolbar(api);

  return api;
}


